<script setup lang="ts">
import { computed, ref, onNuxtReady, useHead, useMediaAutoplay, useImage, useTemplateRef, useDeferredVideoAutoplay } from '#imports'

export interface UIIframeProps {
    src?: string
    /**
     * @description Video URL, preferred over `videoId`. A direct video file
     * (`.mp4`/`.webm`, typically served through Cloudflare Media
     * Transformations `/cdn-cgi/media/…`) renders as a native looping, muted
     * `<video>` — the target of the Stream → media-CDN migration. Any other
     * URL (e.g. a Cloudflare Stream or YouTube embed page) renders as an `<iframe>`, so a CMS
     * field can carry the legacy embed and the new R2 file interchangeably
     * during the migration.
     * @example 'https://media.platego.io/cdn-cgi/media/w=720,q=65,audio=false/landing_pages/dk/spilnu/hero.mp4'
     */
    videoSrc?: string
    /**
     * @description A video Cloudflare Stream ID
     * @example '0934b7c7d911b2d241174084e3e17d6c'
     */
    videoId?: string
    aspectRatio?: `${number}/${number}` | null
    loading?: 'lazy' | 'eager'
    fitParent?: boolean
    altText?: string
    load?: boolean
    /**
     * @description Defer creating the iframe until hydration is idle and the window
     * `load` event has fired, so the Stream player doesn't compete with LCP-critical
     * resources.
     */
    idle?: boolean
    /**
     * @description Poster URL passed to the Stream player. Pass the same URL the page
     * preloads/renders so the player resolves it from the HTTP cache instead of
     * downloading a second thumbnail.
     */
    poster?: string
    preload?: 'auto' | 'metadata' | 'none'
}

const props = withDefaults(defineProps<UIIframeProps>(), {
    aspectRatio: '16/9',
    loading: 'lazy',
    altText: 'Video',
    load: true,
    preload: 'auto',
})

const emit = defineEmits<{
    load: [event: Event]
}>()

const paddingTop = computed(() => {
    if (props.fitParent || !props.aspectRatio) return undefined

    const [width, height] = props.aspectRatio.split('/')

    const percentage = Number(height) / Number(width) /* 16 / 9 Aspect Ratio (divide 9 by 16 = 0.5625) */

    return `${percentage * 100}%`
})

const coverAspectRatio = computed(() => props.aspectRatio ?? '16/9')

// A videoSrc pointing at a real video file gets the native <video>; anything
// else (a Cloudflare Stream or YouTube embed page URL) falls through to the <iframe> branch.
const useNativeVideo = computed(() =>
    !!props.videoSrc && (/\.(?:mp4|webm|mov|m4v)(?:\?|#|$)/i.test(props.videoSrc) || props.videoSrc.includes('/cdn-cgi/media/')))

// media-CDN <video> branch: when autoplay is disallowed (2g), or after the mp4
// fails to load, we drop the <video>'s src (not the element) so its poster
// carries the hero — see the `:src` binding in the template.
const { autoplayAllowed } = useMediaAutoplay()
const videoError = ref(false)
const loadVideoSrc = computed(() => {
    if (videoError.value) return false
    return autoplayAllowed.value
})

// Video embeds honour the same gate; a plain `src` iframe is arbitrary
// content and stays ungated.
const iframeAllowed = computed(() => (!props.videoId && !props.videoSrc) || autoplayAllowed.value)

const videoEl = useTemplateRef<HTMLVideoElement>('videoEl')
useDeferredVideoAutoplay(videoEl)

// The <video> poster is our own R2 asset, so serve it through the Cloudflare
// image transform (`/cdn-cgi/image/`) like the hero tiles. The Stream branch
// keeps `poster` raw — there it's the Stream player's own thumbnail URL.
const img = useImage()
const videoPoster = computed(() => props.poster
    ? img(props.poster, { width: 1280, quality: 50 }, { provider: 'cloudflare' })
    : undefined
)

const cloudflareCustomerStreamUrl = 'customer-sgy3dro64g0stlfq.cloudflarestream.com'

const iframeSrc = computed(() => {
    if (props.videoSrc && !useNativeVideo.value) {
        return props.videoSrc
    }

    if (props.videoId) {
        const poster = props.poster ?? `https://${cloudflareCustomerStreamUrl}/${props.videoId}/thumbnails/thumbnail.jpg?time=&height=600`

        return `https://${cloudflareCustomerStreamUrl}/${props.videoId}/iframe?muted=true&loop=true&autoplay=true&controls=false${props.loading === 'eager' ? '&preload=true' : ''}&poster=${encodeURIComponent(poster)}`
    }

    if (props.src) {
        return props.src
    }

    console.warn('UIIframe component missing src prop or videoId.')
    return ''
})

// Idle defer: hold the iframe back until hydration is idle AND the window
// `load` event has fired — onNuxtReady alone can trigger while the LCP image
// is still streaming on slow connections.
const idleReady = ref(!props.idle)

if (props.idle) {
    onNuxtReady(() => {
        if (document.readyState === 'complete') {
            idleReady.value = true
            return
        }
        window.addEventListener('load', () => {
            idleReady.value = true
        }, { once: true })
    })
}

// Preconnect to Cloudflare Stream custom url. Ships in SSR <head> so the
// TLS handshake warms during HTML parse, even though the iframe element
// itself is deferred to the client by <ClientOnly> in the template below.
if (props.loading === 'eager' && props.videoId && !props.videoSrc) {
    useHead({
        link: [
            {
                rel: 'preconnect',
                href: `https://${cloudflareCustomerStreamUrl}`
            }
        ]
    })
}
</script>

<template>
    <div
        class="ui-iframe"
        :class="{ 'ui-iframe--fit-parent': props.fitParent }"
        :style="{ paddingTop }"
    >
        <video
            v-if="useNativeVideo && props.load"
            ref="videoEl"
            loop
            muted
            playsinline
            disableRemotePlayback
            decoding="async"
            :preload="props.preload"
            :poster="videoPoster"
            :title="altText"
            :src="loadVideoSrc ? props.videoSrc : undefined"
            @error="videoError = true"
        />

        <ClientOnly>
            <iframe
                v-if="!useNativeVideo && props.load && idleReady && iframeAllowed"
                :src="iframeSrc"
                :loading="loading"
                frameborder="0"
                allowfullscreen="true"
                allow="autoplay; encrypted-media; picture-in-picture;"
                :title="altText"
                @load="(e) => emit('load', e)"
            />
        </ClientOnly>
    </div>
</template>

<style scoped lang="scss">
.ui-iframe {
    position: relative;
    width: 100%;
    height: 100%;
    pointer-events: none;
    border-radius: inherit;
    overflow: hidden;

    &--fit-parent {
        position: absolute;
        top: 0;
        left: 0;
    }

    iframe,
    video {
        position: absolute;
        border: 0;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%); /* Center the media */
        /* These ensure the media dimensions scale based on aspect-ratio and min-size */
        width: auto;
        height: auto;
        min-width: 100%;
        min-height: 100%;
        aspect-ratio: v-bind(coverAspectRatio);
        object-fit: cover;
    }
}
</style>
